import {
  Avatar,
  Badge,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import type { FC } from 'react';
import React, { memo, useEffect, useMemo, useState } from 'react';
import type { BatchReceiptDetails } from './ServiceProviderReceiptBatchForm';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { Iconify, Label, OneLineTypography } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import NoReceiptImagesSvg from '../assets/undraw_no_receipt_batch_images.svg';
import {
  isNotEmpty,
  scrollIntoViewIfNecessary,
} from '@pflegenavi/shared/utils';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import {
  MangopaySingleReceiptAmountLabelTooltip,
  useNavigateReceipts,
} from '@pflegenavi/shared-web/receipt-batch';
import type { MangopayReceiptBatchEntry } from '@pflegenavi/service-provider-frontend/api';
import { MangopayPaymentStatus } from '@pflegenavi/service-provider-frontend/api';
import type { PayoutReconciliation } from './ServiceProviderReceiptBatchForm';
import type { ReceiptState } from '@pflegenavi/shared/api';
import { requiresManualSubmissionReceiptState } from '@pflegenavi/shared/api';

interface ReceiptBatchDetailsReceiptsProps {
  receipts: BatchReceiptDetails[];
  receiptsPayments: MangopayReceiptBatchEntry[] | undefined;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  autoGenerateReceipts: boolean;
  onCancelEntry?: (entryId: string) => void;
}

export const ServiceProviderReceiptBatchDetailsReceipts: FC<
  ReceiptBatchDetailsReceiptsProps
> = ({
  receipts,
  receiptsPayments,
  activeIndex,
  setActiveIndex,
  autoGenerateReceipts,
  onCancelEntry,
}) => {
  useNavigateReceipts({
    activeIndex,
    setActiveIndex,
    receiptsLength: receipts.length,
  });

  return (
    <ServiceProviderReceiptBatchDetailsReceiptsView
      receipts={receipts}
      receiptsPayments={receiptsPayments}
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      autoGenerateReceipts={autoGenerateReceipts}
      onCancelEntry={onCancelEntry}
    />
  );
};

export interface ReceiptBatchDetailsReceiptsViewProps
  extends ReceiptBatchDetailsReceiptsProps {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  autoGenerateReceipts: boolean;
}

export const ServiceProviderReceiptBatchDetailsReceiptsView: FC<
  ReceiptBatchDetailsReceiptsViewProps
> = ({
  receipts,
  receiptsPayments,
  activeIndex,
  setActiveIndex,
  autoGenerateReceipts,
  onCancelEntry,
}) => {
  return (
    <ReceiptsViewContainer gap={2}>
      {receipts.map((receipt, index) => {
        const paymentEntry = receiptsPayments?.find(
          (payment) => payment.receipt_batch_entry_id === receipt.id
        );
        return (
          <ServiceProviderReceiptBatchDetailsSingleReceipt
            key={receipt.id}
            index={index}
            entryId={receipt.id!}
            residentName={receipt.residentName ?? '--'}
            individualDate={receipt.individualDate}
            lineItems={receipt.lineItems}
            receiptImageIds={receipt.receiptImageIds}
            onClick={() => setActiveIndex(index)}
            active={index === activeIndex}
            residentActive={receipt.residentActive ?? true}
            autoGenerateReceipts={autoGenerateReceipts}
            MangopayReceiptBatchEntry={paymentEntry}
            payoutReconciliations={receipt.payoutReconciliations}
            onCancelEntry={onCancelEntry}
          />
        );
      })}
    </ReceiptsViewContainer>
  );
};

export interface ServiceProviderReceiptBatchDetailsSingleReceiptProps {
  index: number;
  entryId: string;
  active: boolean;
  residentName: string;
  individualDate?: Date;
  lineItems: BatchReceiptDetails['lineItems'];
  receiptImageIds?: string[] | null;
  onClick?: () => void;
  residentActive: boolean;
  autoGenerateReceipts: boolean;
  MangopayReceiptBatchEntry?: MangopayReceiptBatchEntry;
  payoutReconciliations?: PayoutReconciliation[];
  onCancelEntry?: (entryId: string) => void;
}

export const ServiceProviderReceiptBatchDetailsSingleReceipt: FC<
  ServiceProviderReceiptBatchDetailsSingleReceiptProps
> = ({
  index,
  entryId,
  residentName,
  individualDate,
  lineItems,
  receiptImageIds,
  onClick,
  active,
  residentActive,
  autoGenerateReceipts,
  MangopayReceiptBatchEntry,
  payoutReconciliations,
  onCancelEntry,
}) => {
  const { t } = useTranslation();
  const singleReceiptRef = React.useRef<Element | null>(null);

  useEffect(() => {
    if (active && singleReceiptRef.current) {
      scrollIntoViewIfNecessary(singleReceiptRef.current);
    }
  }, [active]);

  const amount = useMemo(
    () => lineItems.reduce((acc, curr) => acc + (curr.amount ?? 0), 0),
    [lineItems]
  );
  const defaultLineItemName = t('receipts.batch.form.no-line-item-name');
  const notes = useMemo(
    () =>
      lineItems
        .map((item) =>
          isNotEmpty(item.name) ? item.name : defaultLineItemName
        )
        .join(', '),
    [lineItems, defaultLineItemName]
  );

  return (
    <SingleReceiptContainer onClick={onClick} ref={singleReceiptRef}>
      <SingleReceiptContent
        index={index}
        entryId={entryId}
        residentName={residentName}
        amount={amount}
        individualDate={individualDate}
        receiptImageIds={receiptImageIds}
        notes={notes}
        residentActive={residentActive}
        autoGenerateReceipts={autoGenerateReceipts}
        MangopayReceiptBatchEntry={MangopayReceiptBatchEntry}
        payoutReconciliations={payoutReconciliations}
        onCancelEntry={onCancelEntry}
      />
      {active && <ActiveIndicator layoutId="active-card-indicator" />}
    </SingleReceiptContainer>
  );
};

interface SingleReceiptContentProps {
  index: number;
  entryId: string;
  residentName: string;
  residentActive: boolean;
  amount?: number;
  notes?: string;
  receiptImageIds?: string[] | null;
  autoGenerateReceipts: boolean;
  individualDate?: Date;
  MangopayReceiptBatchEntry?: MangopayReceiptBatchEntry;
  payoutReconciliations?: PayoutReconciliation[];
  onCancelEntry?: (entryId: string) => void;
}

const SingleReceiptContent: FC<SingleReceiptContentProps> = memo(
  ({
    index,
    entryId,
    amount,
    residentName,
    notes,
    receiptImageIds,
    residentActive,
    autoGenerateReceipts,
    individualDate,
    MangopayReceiptBatchEntry,
    payoutReconciliations,
    onCancelEntry,
  }) => {
    const fDate = useFormatDate();
    const { t } = useTranslation();
    const theme = useTheme();
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

    // Find the first reconciliation with a successful payout
    const succeededReconciliation = payoutReconciliations?.find(
      (rec) => rec.payout?.state === 'SUCCEEDED'
    );
    const isPaidOut = !!succeededReconciliation;

    const isCancellable =
      onCancelEntry &&
      MangopayReceiptBatchEntry?.status === MangopayPaymentStatus.Paid &&
      !MangopayReceiptBatchEntry?.cancellation;

    return (
      <Stack gap={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                bgcolor: theme.palette.grey['300'],
              }}
            >
              <Typography
                fontSize={15}
                fontWeight={600}
                color="primary"
                sx={{ mb: '-2px' }}
              >
                #{index + 1}
              </Typography>
            </Avatar>
            <Stack alignItems="flex-start" gap={0.3}>
              <OneLineTypography
                variant="subtitle2"
                // put explicitly width here because the alignItems="flex-start" in the parent stack affects the width of the child
                sx={{
                  width: 200,
                }}
                fontWeight={700}
              >
                {residentName}
              </OneLineTypography>
              <SingleReceiptAmountLabel
                amount={amount ?? 0}
                paymentStatus={MangopayReceiptBatchEntry?.status}
                hasPendingTransfer={
                  MangopayReceiptBatchEntry?.has_pending_transfer
                }
                cancellation={MangopayReceiptBatchEntry?.cancellation}
                receiptState={MangopayReceiptBatchEntry?.receipt_state}
              />
              {isPaidOut && (
                <Tooltip
                  title={`${t(
                    'service-provider-app.receipt-batch-overview-table.paid-out'
                  )}${
                    succeededReconciliation?.payout?.transferred
                      ? ` • ${fDate(
                          new Date(succeededReconciliation.payout.transferred),
                          YEAR_MONTH_DAY_SHORT_FORMAT
                        )}`
                      : ''
                  }${
                    succeededReconciliation?.payout?.iban_last_4
                      ? ` • IBAN **** ${succeededReconciliation.payout.iban_last_4}`
                      : ''
                  }`}
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Iconify
                      icon="mdi:check-circle"
                      color={theme.palette.success.main}
                      width={14}
                      height={14}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: theme.palette.success.main,
                        fontWeight: 500,
                      }}
                    >
                      {t(
                        'service-provider-app.receipt-batch-overview-table.paid-out'
                      )}
                    </Typography>
                  </Stack>
                </Tooltip>
              )}
              {individualDate && (
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'text.secondary',
                  }}
                >
                  {fDate(new Date(individualDate), YEAR_MONTH_DAY_SHORT_FORMAT)}
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            {receiptImageIds && receiptImageIds.length > 0 ? (
              <div style={{ position: 'relative' }}>
                <Badge
                  badgeContent={receiptImageIds.length}
                  invisible={receiptImageIds.length < 2}
                  color="info"
                  sx={{
                    '& .MuiBadge-badge': { fontSize: 12 },
                    left: 30,
                    top: -28,
                  }}
                />
                <Iconify
                  icon="mdi:receipt-text"
                  color={theme.palette.success.main}
                  fontSize={35}
                />
              </div>
            ) : autoGenerateReceipts ? (
              <Tooltip
                title={t('receipts.batch.one-receipt-will-be-auto-generated')}
              >
                <div>
                  <Iconify
                    icon="mdi:receipt-text"
                    color={theme.palette.info.main}
                    fontSize={35}
                  />
                </div>
              </Tooltip>
            ) : (
              <Tooltip
                title={t('receipts.batch.form.receipt-image-unavailable')}
              >
                <div>
                  <Iconify
                    icon="mdi:receipt-text"
                    color={theme.palette.grey['400']}
                    fontSize={35}
                  />
                </div>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        {(notes || isCancellable) && (
          <Stack direction="row" alignItems="center">
            {notes && <Notes notes={notes} />}
            {isCancellable && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuAnchorEl(e.currentTarget);
                  }}
                  sx={{ ml: 'auto', flexShrink: 0 }}
                >
                  <Iconify icon="eva:more-vertical-fill" width={20} />
                </IconButton>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={(e: React.SyntheticEvent) => {
                    e.stopPropagation?.();
                    setMenuAnchorEl(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuAnchorEl(null);
                      onCancelEntry(entryId);
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    {t('receipts.batch.entry.cancel-action')}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        )}
      </Stack>
    );
  }
);

interface SingleReceiptAmountLabelProps {
  amount: number;
  paymentStatus: MangopayPaymentStatus | undefined;
  hasPendingTransfer?: boolean;
  cancellation?: { date: Date; note: string };
  receiptState?: ReceiptState | null;
}

const SingleReceiptAmountLabel: FC<SingleReceiptAmountLabelProps> = ({
  amount,
  paymentStatus,
  hasPendingTransfer,
  cancellation,
  receiptState,
}) => {
  const { fCurrencyCents } = useFormatting();
  const { t } = useTranslation();

  if (paymentStatus === MangopayPaymentStatus.ReceiptDeleted) {
    return (
      <Label color="error" variant="ghost">
        {fCurrencyCents(amount)} ({t('receipts.batch.receipt-deleted')})
      </Label>
    );
  }

  if (cancellation) {
    return (
      <Tooltip
        title={
          cancellation.note || t('receipts.batch.details.payment.cancelled')
        }
      >
        <span>
          <Label color="error" variant="ghost">
            {fCurrencyCents(amount)} (
            {t('receipts.batch.details.payment.cancelled')})
          </Label>
        </span>
      </Tooltip>
    );
  }

  if (paymentStatus === MangopayPaymentStatus.Pending) {
    const unpaidLabel = (
      <Label color="warning" variant="ghost">
        {fCurrencyCents(amount)} ({t('receipts.batch.details.payment.not-paid')}
        )
      </Label>
    );

    // The tooltip explains the payment as blocked by a low resident balance.
    // That is not the reason when the institution still has to submit the
    // receipt, so show the plain label instead of a wrong explanation.
    if (requiresManualSubmissionReceiptState(receiptState)) {
      return unpaidLabel;
    }

    return (
      <MangopaySingleReceiptAmountLabelTooltip
        hasPendingTransfer={hasPendingTransfer ?? false}
      >
        {unpaidLabel}
      </MangopaySingleReceiptAmountLabelTooltip>
    );
  }

  return (
    <Label color="success" variant="ghost">
      {fCurrencyCents(amount)}
    </Label>
  );
};

interface NotesProps {
  notes: string | null;
}

const Notes: FC<NotesProps> = ({ notes }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={notes} arrow placement="bottom">
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          px: 2,
          py: 1,
          backgroundColor: 'grey.200',
          borderRadius: 2,
        }}
      >
        <Typography
          variant="caption"
          fontSize={13.5}
          fontWeight={600}
          color="text.secondary"
        >
          {t('receipts.batch.note')}
        </Typography>
        <OneLineTypography variant="caption" fontSize={13.5} color="grey.800">
          {notes}
        </OneLineTypography>
      </Stack>
    </Tooltip>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const NoReceiptsAdded = () => {
  const { t } = useTranslation();
  return (
    <Stack
      alignItems="center"
      sx={{
        p: 3,
        flex: 1,
      }}
    >
      <Stack
        alignItems="center"
        sx={{
          flex: 1,
        }}
        gap={2}
      >
        <img src={NoReceiptImagesSvg} width={200} />
        <Stack alignItems="center" gap={1}>
          <Typography
            textTransform="uppercase"
            textAlign="center"
            fontWeight={700}
          >
            {t('receipts.batch.no-receipt-available')}
          </Typography>
          <Typography textAlign="center">
            {t('receipts.batch.no-receipt-available-description')}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

const ReceiptsViewContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    overflowY: 'auto',
    maxHeight: '70vh',
    width: 350,
    overflowX: 'hidden',
    direction: 'rtl',
    pl: 1,
    ml: -1,
    pb: 2,
    '&::-webkit-scrollbar': {
      width: '0.3em',
      color: 'red',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0,0,0,.1)',
    },
    position: 'relative',
    zIndex: 0,
  })
);

const SingleReceiptContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'withNotes',
})(
  ({ theme }) =>
    ({ theme }) =>
      theme.unstable_sx({
        position: 'relative',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        boxShadow: theme.customShadows.card,
        borderRadius: 2,
        p: 2.5,
        width: 340,
        maxHeight: 250,
        cursor: 'pointer',
        userSelect: 'none',
        direction: 'ltr',
        overflow: 'visible',
        zIndex: undefined,
      })
);

const ActiveIndicator = styled(motion.div)(({ theme }) =>
  theme.unstable_sx({
    backgroundColor: 'success.main',
    width: 5,
    right: 0,
    top: 0,
    bottom: 0,
    position: 'absolute',
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  })
);
